
import React, { useState, useEffect, useCallback } from 'react';
import {
  unstable_HistoryRouter as HistoryRouter,
  Routes,
  Route,
  useLocation
} from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import theme from './theme';
import { apiUrl, configureApiFetch } from './utils/api';
import Sidebar from './components/Sidebar';
import UserMenu from './components/UserMenu';
import { usePendingCounts } from './hooks/usePendingCounts';
import LoadingOverlay from './components/LoadingOverlay';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

import Dashboard from './pages/Dashboard';
import DesignChanges from './pages/DesignChanges';
import Documents from './pages/Documents';
import DocumentSettings from './pages/DocumentSettings';
import Assignments from './pages/Assignments';
import SiteTeam from './pages/SiteTeam';
import QSTeam from './pages/QSTeam';
import SiteDashboard from './pages/SiteDashboard';
import QSDashboard from './pages/QSDashboard';
import MaterialRequisitions from './pages/MaterialRequisitions';
import QSPurchaseOrders from './pages/QSPurchaseOrders';
import QSContractOrVO from './pages/QSContractOrVO';
import ContractVOApproval from './pages/ContractVOApproval';

import BusinessContacts from './pages/BusinessContacts';
import Contracts from './pages/Contracts';
import BudgetDashboard from './pages/BudgetDashboard';
import Users from './pages/Users';
import Groups from './pages/Groups';
import Projects from './pages/Projects';
import Companies from './pages/Companies';
import AuditLogs from './pages/AuditLogs';
import DocumentTemplates from './pages/DocumentTemplates';
import BaseScopeCategories from './pages/BaseScopeCategories';
import Login from './pages/Login';
import ForcePasswordChange from './pages/ForcePasswordChange';
import SetupAccount from './pages/SetupAccount';
import ResetPassword from './pages/ResetPassword';
import WinstonChat from './pages/WinstonChat';
import AISettings from './pages/AISettings';
import Units from './pages/Units';
import BudgetLineItems from './pages/BudgetLineItems';
import UnitRate from './pages/UnitRate';
import ContractDesignChanges from './pages/ContractDesignChanges';
import PMDashboard from './pages/PMDashboard';

const history = createBrowserHistory();

const getSectionTitle = pathname => {
  const path = (pathname || '/').toLowerCase();
  if (path === '/' || path === '/dashboard') return 'Design Dashboard';
  if (path.startsWith('/design-changes')) return 'Design Changes';
  if (path.startsWith('/document-settings')) return 'Document Settings';
  if (path.startsWith('/document-templates')) return 'Document Templates';
  if (path.startsWith('/documents')) return 'Documents';
  if (path.startsWith('/assignments')) return 'Assignments';
  if (path.startsWith('/pm-dashboard')) return 'PM Dashboard';
  if (path.startsWith('/site-dashboard')) return 'Site Dashboard';
  if (path.startsWith('/site-team')) return 'Site Tracker';
  if (path.startsWith('/qs-dashboard')) return 'QS Dashboard';
  if (path.startsWith('/qs-team')) return 'QS Design Change';
  if (path.startsWith('/qs-contract-or-vo')) return 'QS Contracts & VOs';
  if (path.startsWith('/qs-purchase-orders')) return 'QS Purchase Orders';
  if (path.startsWith('/contract-design-changes')) return 'Contract Design Changes';
  if (path.startsWith('/contract-vo-approval')) return 'Contract / VO Approval';
  if (path.startsWith('/contracts')) return 'Contracts & VOs';
    if (path.startsWith('/mr-po-approval')) return 'MR / PO Approval';

  if (path.startsWith('/material-requisitions')) return 'Material Requisitions';
  if (path.startsWith('/purchase-orders')) return 'Purchase Orders';
  if (path.startsWith('/units')) return 'Units';
  if (path.startsWith('/budget-line-items')) return 'Budget Line Items';
  if (path.startsWith('/unit-rate')) return 'Unit Rate';
  if (path.startsWith('/base-scope-categories')) return 'BOQ Categories';
  if (path.startsWith('/projects')) return 'Projects';
  if (path.startsWith('/users')) return 'Users';
  if (path.startsWith('/groups')) return 'Groups';
  if (path.startsWith('/companies')) return 'Companies';
  if (path.startsWith('/audit-logs')) return 'Audit Logs';
  if (path.startsWith('/business-contacts')) return 'Business Contacts';
  if (path.startsWith('/ai-assistant')) return 'AI Assistant';
  if (path.startsWith('/ai-settings')) return 'AI Settings';
  return 'PM System PRO';
};

function AppLayout({
  user,
  onLogout,
  projectId,
  setProjectId,
  projects,
  companyId,
  setCompanyId,
  companies,
  permissions,
  isGlobalAdmin,
  fetchProjects,
  refreshAccess
}) {
  const location = useLocation();
  const sectionTitle = getSectionTitle(location.pathname);
  const { counts: pendingCounts } = usePendingCounts(projectId);

  useEffect(() => {
    document.title = `PM System PRO / ${sectionTitle}`;
  }, [sectionTitle]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar
        projectId={projectId}
        setProjectId={setProjectId}
        projects={projects}
        companyId={companyId}
        setCompanyId={setCompanyId}
        companies={companies}
        permissions={permissions}
        isGlobalAdmin={isGlobalAdmin}
        pendingCounts={pendingCounts}
        userContext={{
          user,
          permissions: user?.context?.permissions || { can_manage_companies: isGlobalAdmin },
          highest_role: user?.context?.highest_role || (isGlobalAdmin ? 'global_admin' : 'user')
        }}
      />
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <UserMenu user={user} onLogout={onLogout} isGlobalAdmin={isGlobalAdmin} sectionTitle={sectionTitle} />
        <Box sx={{ flexGrow: 1, pt: { xs: '72px', md: '96px' }, px: 0 }}>
          {/* Check if user has any access */}
          {!isGlobalAdmin && !Object.values(permissions || {}).some(perm => perm !== 'none') ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              px: 3
            }}>
              <Box sx={{
                maxWidth: 500,
                p: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
                  No Access
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                  You are not assigned to any groups and have no access to any resources.
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  Please contact your administrator to request access.
                </Typography>
              </Box>
            </Box>
          ) : (
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute
                  element={<Dashboard projectId={projectId} access={permissions['Dashboard - Design']} />}
                  access={permissions['Dashboard - Design']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/design-changes"
              element={
                <ProtectedRoute
                  element={
                    <DesignChanges
                      projectId={projectId}
                      access={permissions['Design Changes']}
                      deleteAllAccess={permissions['Delete All Design Changes']}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['Design Changes']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute
                  element={
                    <Documents
                      projectId={projectId}
                      access={permissions['Documents']}
                      deleteAllAccess={permissions['Delete All Documents']}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['Documents']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/document-settings"
              element={
                <ProtectedRoute
                  element={
                    <DocumentSettings
                      projectId={projectId}
                      access={permissions['Document Settings']}
                      deleteLocationAccess={permissions['Delete All Document Locations']}
                      deleteCategoryAccess={permissions['Delete All Document Categories']}
                      deleteStatusAccess={permissions['Delete All Document Statuses']}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['Document Settings']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/assignments"
              element={
                <ProtectedRoute
                  element={
                    <Assignments
                      projectId={projectId}
                      access={permissions['Assignments']}
                      deleteAllAccess={permissions['Delete All Assignments']}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['Assignments']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/site-dashboard"
              element={
                <ProtectedRoute
                  element={<SiteDashboard projectId={projectId} access={permissions['Dashboard - Site']} />}
                  access={permissions['Dashboard - Site']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/site-team"
              element={
                <ProtectedRoute
                  element={
                    <SiteTeam
                      projectId={projectId}
                      access={permissions['Site Tracker']}
                    />
                  }
                  access={permissions['Site Tracker']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/material-requisitions"
              element={
                <ProtectedRoute
                  element={
                    <MaterialRequisitions
                      projectId={projectId}
                      access={permissions['Material Requisitions']}
                      deleteAllAccess={permissions['Delete All Material Requisitions']}
                      userId={user.user_id}
                      projects={projects}
                    />
                  }
                  access={permissions['Material Requisitions']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/qs-purchase-orders"
              element={
                <ProtectedRoute
                  element={
                    <QSPurchaseOrders
                      projectId={projectId}
                      access={permissions['QS Purchase Orders']}
                      userId={user.user_id}
                      projects={projects}
                    />
                  }
                  access={permissions['QS Purchase Orders']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/qs-dashboard"
              element={
                <ProtectedRoute
                  element={<QSDashboard projectId={projectId} access={permissions['Dashboard - QS']} />}
                  access={permissions['Dashboard - QS']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/qs-team"
              element={
                <ProtectedRoute
                  element={
                    <QSTeam
                      projectId={projectId}
                      access={permissions['QS Design Change']}
                      projects={projects}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['QS Design Change']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/qs-contract-or-vo"
              element={
                <ProtectedRoute
                  element={
                    <QSContractOrVO
                      projectId={projectId}
                      access={permissions['QS Contract or VO'] || 'none'}
                      deleteAllAccess={permissions['Delete All QS Contract or VO'] || 'none'}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['QS Contract or VO'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/contracts"
              element={
                <ProtectedRoute
                  element={
                    <Contracts
                      projectId={projectId}
                      access={permissions['Contracts'] || 'none'}
                      deleteAllAccess={permissions['Delete All Contracts'] || 'none'}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['Contracts'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/contract-design-changes"
              element={
                <ProtectedRoute
                  element={
                    <ContractDesignChanges
                      projectId={projectId}
                      access={permissions['Contract Design Changes'] || 'none'}
                      deleteAllAccess={permissions['Delete All Contract Design Changes'] || 'none'}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['Contract Design Changes'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/mr-po-approval"
              element={
                <ProtectedRoute
                  element={
                    <BudgetDashboard
                      projectId={projectId}
                      access={permissions['MR/PO Approval']}
                    />
                  }
                  access={permissions['MR/PO Approval']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/contract-vo-approval"
              element={
                <ProtectedRoute
                  element={
                    <ContractVOApproval
                      projectId={projectId}
                      access={permissions['Contract/VO Approval'] || 'none'}
                      userId={user.user_id}
                      projects={projects}
                    />
                  }
                  access={permissions['Contract/VO Approval'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            {/* <Route
              element={
                <PurchaseOrders
                  projectId={projectId}
                  access={permissions['Purchase Orders'] || 'none'}
                  userId={user.user_id}
                />
              }
            /> */}
            <Route
              path="/business-contacts"
              element={
                <ProtectedRoute
                  element={
                    <BusinessContacts
                      access={permissions['Business Contacts'] || 'none'}
                      isGlobalAdmin={isGlobalAdmin}
                      userContext={user?.context}
                    />
                  }
                  access={permissions['Business Contacts'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/units"
              element={
                <ProtectedRoute
                  element={
                    <Units
                      access={permissions['Units'] || 'none'}
                      deleteAllAccess={permissions['Delete All Units'] || 'none'}
                    />
                  }
                  access={permissions['Units'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/budget-line-items"
              element={
                <ProtectedRoute
                  element={
                    <BudgetLineItems
                      projectId={projectId}
                      access={permissions['BOQ Line Items'] || 'none'}
                      deleteAllAccess={permissions['Delete All BOQ Line Items'] || permissions['BOQ Line Items'] || 'none'}
                    />
                  }
                  access={permissions['BOQ Line Items'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/unit-rate"
              element={
                <ProtectedRoute
                  element={
                    <UnitRate
                      projectId={projectId}
                      access={permissions['Unit Rate'] || 'none'}
                      deleteAllAccess={permissions['Delete All Rate Sheets'] || 'none'}
                      deleteAllItemsAccess={permissions['Delete All Rate Sheets items'] || 'none'}
                    />
                  }
                  access={permissions['Unit Rate'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute
                  element={
                    <Projects
                      projects={projects}
                      fetchProjects={fetchProjects}
                      access={permissions['Projects'] || 'none'}
                      projectId={projectId}
                      companyId={companyId}
                    />
                  }
                  access={permissions['Projects'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/base-scope-categories"
              element={
                <ProtectedRoute
                  element={
                    <BaseScopeCategories
                      projectId={projectId}
                      access={permissions['BOQ Categories'] || 'none'}
                      deleteAllAccess={permissions['Delete All BOQ Categories'] || 'none'}
                      userId={user.user_id}
                    />
                  }
                  access={permissions['BOQ Categories'] || 'none'}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route path="/users" element={
              <ProtectedRoute
                element={
                  <Users
                    access={permissions['Users'] || 'none'}
                    companyId={companyId}
                    isGlobalAdmin={isGlobalAdmin}
                    currentUser={user}
                  />
                }
                access={permissions['Users'] || 'none'}
                isGlobalAdmin={isGlobalAdmin}
              />
            } />
            <Route path="/companies" element={
              <ProtectedRoute
                element={<Companies access={permissions['Companies']} />}
                access={permissions['Companies']}
                isGlobalAdmin={isGlobalAdmin}
              />
            } />
            <Route path="/audit-logs" element={
              <ProtectedRoute
                element={<AuditLogs access={isGlobalAdmin ? 'admin' : 'none'} />}
                access={isGlobalAdmin ? 'admin' : 'none'}
                isGlobalAdmin={isGlobalAdmin}
              />
            } />
            <Route path="/document-templates" element={
              <ProtectedRoute
                element={<DocumentTemplates access={isGlobalAdmin ? 'admin' : permissions['Companies'] || 'none'} />}
                access={isGlobalAdmin ? 'admin' : permissions['Companies'] || 'none'}
                isGlobalAdmin={isGlobalAdmin}
              />
            } />
            <Route path="/groups" element={
              <ProtectedRoute
                element={
                  <Groups
                    access={permissions['Groups'] || 'none'}
                    refreshAccess={refreshAccess}
                    companyId={companyId}
                  />
                }
                access={permissions['Groups'] || 'none'}
                isGlobalAdmin={isGlobalAdmin}
              />
            } />
            <Route
              path="/ai-assistant"
              element={
                <ProtectedRoute
                  element={
                    <WinstonChat
                      projectId={projectId}
                      access={permissions['AI Assistant - Chat']}
                    />
                  }
                  access={permissions['AI Assistant - Chat']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />
            <Route
              path="/ai-settings"
              element={
                <ProtectedRoute
                  element={
                    <AISettings
                      projectId={projectId}
                      access={permissions['AI Assistant - Settings']}
                      isGlobalAdmin={isGlobalAdmin}
                    />
                  }
                  access={permissions['AI Assistant - Settings']}
                  isGlobalAdmin={isGlobalAdmin}
                />
              }
            />

{/* ✅ ADD THIS */}
<Route
  path="/pm-dashboard"
  element={
    <ProtectedRoute
      element={<PMDashboard projectId={projectId} access={permissions['Dashboard - Design'] || 'read'} />}
      access={permissions['Dashboard - Design'] || 'read'}
      isGlobalAdmin={isGlobalAdmin}
    />
  }
/>
</Routes>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [access, setAccess] = useState({ groups: [], permissions: {} });
  const [projectId, setProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [rememberMe, setRememberMe] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [accessRemoved, setAccessRemoved] = useState(false);
  // True when the logged-in user must change their password before they
  // can use the rest of the app (forced on admin-created accounts and
  // after admin-driven password resets). Cleared once they pick a new
  // password on the ForcePasswordChange screen.
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const isGlobalAdmin = access.groups.some(g => g.group_name?.toLowerCase() === 'global admin');

  useEffect(() => {
    const initializeApp = async () => {
      configureApiFetch();

      try {
        let sessionData = null;

        const sessionStorageData = sessionStorage.getItem('session');

        if (sessionStorageData) {
          try {
            sessionData = JSON.parse(sessionStorageData);
          } catch (e) {
            sessionStorage.removeItem('session');
          }
        }

        if (!sessionData) {
          const localData = localStorage.getItem('session');

          if (localData) {
            try {
              const parsedData = JSON.parse(localData);

              const sessionAge = Date.now() - (parsedData.timestamp || 0);
              // If rememberMe: 30 days, otherwise 12 hours (for cross-tab support in same browser session)
              const maxSessionAge = parsedData.rememberMe
                ? 30 * 24 * 60 * 60 * 1000  // 30 days
                : 12 * 60 * 60 * 1000;       // 12 hours

              if (sessionAge < maxSessionAge) {
                sessionData = parsedData;
                sessionStorage.setItem('session', localData);
              } else {
                localStorage.removeItem('session');
              }
            } catch (e) {
              localStorage.removeItem('session');
            }
          }
        }
        
        if (sessionData && sessionData.user) {
          setUser(sessionData.user);
          setAccess(sessionData.access || { groups: [], permissions: {} });
          setRememberMe(sessionData.rememberMe || false);
          setLoggedIn(true);
          setMustChangePassword(!!sessionData.user.must_change_password);

          if (sessionData.companyId) {
            setCompanyId(sessionData.companyId);
          }

          if (sessionData.projectId) {
            setProjectId(sessionData.projectId);
          }

          // Refresh user data to get latest context
          fetch(apiUrl('/api/users/me'), {
            credentials: 'include'
          })
            .then(res => res.json())
            .then(data => {
              setUser(data);
              // Re-sync the forced-change flag from the authoritative
              // backend response in case it changed since the cached
              // session was written.
              setMustChangePassword(!!data.must_change_password);
              console.log('User context loaded:', data.context);
              // Permissions will be loaded via refreshAccess useEffect
            })
            .catch(err => console.error('Failed to refresh user context:', err));
        }
      } catch (error) {
        console.error('App initialization error:', error);
      }

      setIsInitialized(true);
    };

    initializeApp();
  }, []);

  const fetchProjects = useCallback(() => {
    if (!user) return Promise.resolve();
    return fetch(apiUrl(`/api/users/${user.user_id}/projects`), {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setProjects(data.projects || []))
      .catch(() => setProjects([]));
  }, [user]);

  const fetchCompanies = useCallback(() => {
    if (!user) return Promise.resolve();
    return fetch(apiUrl('/api/companies'), {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setCompanies(data.companies || []))
      .catch(() => setCompanies([]));
  }, [user]);

  const refreshUserData = useCallback(() => {
    if (!user) return Promise.resolve();
    return fetch(apiUrl('/api/users/me'), {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        setUser(data);
        console.log('User context refreshed:', data.context);
      })
      .catch(err => console.error('Failed to refresh user data:', err));
  }, [user]);

  const refreshAccess = useCallback((projId = null) => {
    if (!user) return Promise.resolve();
    // Don't clear access immediately - wait for new data to arrive
    const query = projId !== null ? `?project_id=${projId}` : '';
    return fetch(apiUrl(`/api/users/${user.user_id}/groups${query}`), {
      credentials: 'include'  // Include cookies for session auth
    })
      .then(res => res.json())
      .then(grps => {
        const groupIds = grps.map(g => g.group_id);
        if (groupIds.length === 0) {
          // No groups, no permissions - user should be logged out
          console.log('[refreshAccess] No groups found, marking access as removed');
          setAccess({ groups: [], permissions: {} });
          setAccessRemoved(true); // Trigger logout in useEffect
          return;
        }
        // Use batch endpoint to fetch all permissions in a single request
        return fetch(apiUrl(`/api/groups/batch-permissions?group_ids=${groupIds.join(',')}`), {
          credentials: 'include'
        })
          .then(r => r.json())
          .then(batchResult => {
            const order = { none: 0, read: 1, write: 2 };
            const perms: Record<string, string> = {};

            // Process permissions from batch result
            Object.values(batchResult).forEach((permsList: any) => {
              if (Array.isArray(permsList)) {
                permsList.forEach((p: any) => {
                  if (!perms[p.page_name] || order[p.access_type] > order[perms[p.page_name]]) {
                    perms[p.page_name] = p.access_type;
                  }
                });
              }
            });

            // Check if user is Global Admin - they get write access to everything
            const isGlobalAdmin = grps.some(g => g.group_name?.toLowerCase() === 'global admin');
            if (isGlobalAdmin) {
              // Grant write access to all pages for Global Admin
              const allPages = [
                'Dashboard - Design', 'Design Changes', 'Delete All Design Changes',
                'Documents', 'Delete All Documents', 'Delete All Document Locations',
                'Delete All Document Categories', 'Delete All Document Statuses',
                'Assignments', 'Delete All Assignments', 'Dashboard - Site', 'Site Tracker',
                'Material Requisitions', 'Delete All Material Requisitions', 'Purchase Orders',
                'Delete All Purchase Orders', 'Users', 'Groups', 'Projects', 'Companies',
                'Audit Logs', 'Document Templates', 'BOQ Categories',
                'Delete All BOQ Line Items', 'Units', 'BOQ Line Items', 'Unit Rates',
                'BOQ', 'Contract/DC', 'VO', 'AI Settings'
              ];
              allPages.forEach(page => {
                perms[page] = 'write';
              });
            }

            setAccess({ groups: grps, permissions: perms });
          })
          .catch(console.error);
      })
      .catch(console.error);
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshAccess(projectId);
    }
  }, [user, projectId]); // Removed refreshAccess from dependencies to prevent infinite loop

  useEffect(() => {
    // Only run once on initial mount when logged in and data not loaded
    if (loggedIn && user && !projects.length && !companies.length && !isLoadingData) {
      setIsLoadingData(true);
      Promise.all([
        fetchProjects(),
        fetchCompanies()
      ]).catch((err) => {
        console.error('Error loading initial data:', err);
      }).finally(() => {
        setIsLoadingData(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, user]);

  // Listen for company updates to refresh companies list
  useEffect(() => {
    const handleCompanyUpdate = () => {
      if (user) {
        fetchCompanies();
      }
    };

    window.addEventListener('companyUpdated', handleCompanyUpdate);
    return () => {
      window.removeEventListener('companyUpdated', handleCompanyUpdate);
    };
  }, [user, fetchCompanies]);

  // Auto-select company for non-global admins with single company access
  useEffect(() => {
    if (user && companies.length > 0 && !companyId && !isGlobalAdmin) {
      // If user has access to only one company, auto-select it
      if (companies.length === 1) {
        console.log('[App] Auto-selecting single company:', companies[0].company_id);
        setCompanyId(companies[0].company_id);
      } else if (user.primary_company_id) {
        // If user has a primary company set, use that
        console.log('[App] Auto-selecting primary company:', user.primary_company_id);
        setCompanyId(user.primary_company_id);
      }
    }
  }, [user, companies, companyId, isGlobalAdmin]);

  // Handle showing Access Denied when user's access is removed
  // ProtectedRoute will automatically show Access Denied page when permissions are empty
  // This allows admin to reassign groups without user needing to re-login
  useEffect(() => {
    if (accessRemoved) {
      console.log('[App] User has no groups - Access Denied will be shown by ProtectedRoute');
      // No need to logout - just let ProtectedRoute handle showing Access Denied
      setAccessRemoved(false); // Reset flag
    }
  }, [accessRemoved]);

  const handleLogin = (data: any, remember: boolean) => {
    const order: Record<string, number> = { none: 0, read: 1, write: 2 };
    const perms: Record<string, string> = {};
    (data.groups || []).forEach((g: any) => {
      const gPerms = (data.permissions || {})[g.group_id] || {};
      Object.entries(gPerms).forEach(([page, level]) => {
        const levelStr = level as string;
        if (!perms[page] || order[levelStr] > order[perms[page]]) {
          perms[page] = levelStr;
        }
      });
    });

    // Check if user is Global Admin - they get write access to everything
    const isGlobalAdmin = (data.groups || []).some((g: any) => g.group_name?.toLowerCase() === 'global admin');
    if (isGlobalAdmin) {
      // Grant write access to all pages for Global Admin
      const allPages = [
        'Dashboard - Design', 'Design Changes', 'Delete All Design Changes',
        'Documents', 'Delete All Documents', 'Delete All Document Locations',
        'Delete All Document Categories', 'Delete All Document Statuses',
        'Assignments', 'Delete All Assignments', 'Dashboard - Site', 'Site Tracker',
        'Material Requisitions', 'Delete All Material Requisitions', 'Purchase Orders',
        'Delete All Purchase Orders', 'Users', 'Groups', 'Projects', 'Companies',
        'Audit Logs', 'Document Templates', 'BOQ Categories',
        'Delete All BOQ Line Items', 'Units', 'BOQ Line Items', 'Unit Rates',
        'BOQ', 'Contract/DC', 'VO', 'AI Settings'
      ];
      allPages.forEach(page => {
        perms[page] = 'write';
      });
    }

    const accessData = { groups: data.groups || [], permissions: perms };

    // Set all state
    setUser(data.user);
    setAccess(accessData);
    setLoggedIn(true);
    setRememberMe(remember);
    setMustChangePassword(!!data.user?.must_change_password);
    
    // Create session data
    const sessionData = {
      user: data.user,
      access: accessData,
      rememberMe: remember,
      timestamp: Date.now()
    };

    const sessionJSON = JSON.stringify(sessionData);

    // Always save to localStorage for cross-tab functionality
    // rememberMe only controls expiry duration (30 days vs 12 hours)
    localStorage.setItem('session', sessionJSON);
    sessionStorage.setItem('session', sessionJSON);
  };

  // Update session storage when project or company changes
  const updateSession = useCallback(() => {
    if (loggedIn && user) {
      const sessionData = {
        user,
        access,
        projectId,
        companyId,
        rememberMe,
        timestamp: Date.now()
      };
      const payload = JSON.stringify(sessionData);

      // Always update both sessionStorage and localStorage for cross-tab functionality
      // localStorage works across tabs, sessionStorage does not
      sessionStorage.setItem('session', payload);
      localStorage.setItem('session', payload);
    }
  }, [loggedIn, user, access, projectId, companyId, rememberMe]);

  // Update session storage whenever projectId changes
  useEffect(() => {
    updateSession();
  }, [updateSession]);

  // Listen for storage changes from other tabs (for cross-tab logout)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If session was removed from localStorage in another tab, logout from this tab too
      if (e.key === 'session' && e.newValue === null && loggedIn) {
        console.log('Session removed in another tab, logging out...');
        // CRITICAL: Also remove from sessionStorage to prevent re-login on refresh!
        sessionStorage.removeItem('session');
        setLoggedIn(false);
        setUser(null);
        setAccess({ groups: [], permissions: {} });
        setProjectId(null);
        setCompanyId(null);
        setRememberMe(false);
      }
    };

    // Add storage event listener
    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loggedIn]);

  // Auto-logout when backend returns 401 (session expired)
  useEffect(() => {
    const handleSessionExpired = () => {
      if (!loggedIn) return;
      sessionStorage.removeItem('session');
      localStorage.removeItem('session');
      setLoggedIn(false);
      setUser(null);
      setAccess({ groups: [], permissions: {} });
      setProjectId(null);
      setCompanyId(null);
      setRememberMe(false);
    };
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [loggedIn]);

  const handleLogout = () => {
    sessionStorage.removeItem('session');
    localStorage.removeItem('session');
    setLoggedIn(false);
    setUser(null);
    setAccess({ groups: [], permissions: {} });
    setProjectId(null);
    setCompanyId(null);
    setRememberMe(false);
    setMustChangePassword(false);
  };

  // Show loading until app is initialized
  if (!isInitialized) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <CircularProgress size={50} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {!loggedIn && window.location.pathname === '/setup-account' ? (
        // Public magic-link page: a newly created user lands here from their
        // welcome email to set a password and finish account setup. On
        // success it calls handleLogin and redirects to the dashboard.
        <SetupAccount onLogin={handleLogin} />
      ) : window.location.pathname === '/reset-password' ? (
        // Public forgot-password page: user lands here from the reset email
        // to choose a new password, then signs in again normally.
        <ResetPassword />
      ) : !loggedIn ? (
        <Login onLogin={handleLogin} />
      ) : mustChangePassword ? (
        // The user has logged in (and verified MFA) but is flagged to
        // change their password before they can use the app. Render the
        // ForcePasswordChange screen instead of mounting the router so
        // they can't navigate around the gate.
        <ForcePasswordChange
          user={user}
          onLogout={handleLogout}
          onPasswordChanged={(updatedUser) => {
            setUser(updatedUser);
            setMustChangePassword(false);
            // Keep the cached session in sync so a refresh doesn't bring
            // the user straight back to this screen.
            try {
              const stored =
                sessionStorage.getItem('session') ||
                localStorage.getItem('session');
              if (stored) {
                const parsed = JSON.parse(stored);
                parsed.user = updatedUser;
                parsed.timestamp = Date.now();
                const payload = JSON.stringify(parsed);
                sessionStorage.setItem('session', payload);
                localStorage.setItem('session', payload);
              }
            } catch (e) {
              console.error('Failed to update cached session', e);
            }
          }}
        />
      ) : (
        <>
          <HistoryRouter
            history={history as any}
            future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
          >
            <AppLayout
              user={user}
              onLogout={handleLogout}
              projectId={projectId}
              setProjectId={setProjectId}
              projects={projects}
              companyId={companyId}
              setCompanyId={setCompanyId}
              companies={companies}
              permissions={access.permissions}
              isGlobalAdmin={isGlobalAdmin}
              fetchProjects={fetchProjects}
              refreshAccess={refreshAccess}
            />
          </HistoryRouter>
          {/* Only show global loader on very first data load, not on subsequent navigations */}
          <LoadingOverlay
            loading={isLoadingData && projects.length === 0 && companies.length === 0}
            size={60}
            fullScreen={true}
            zIndex={9999}
          />
        </>
      )}
    </ThemeProvider>
  );
}

export default App;
